import React, { useState, useEffect, useMemo } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const HEATMAP_SCALE = [[0, '#dc2626'], [0.5, '#ffffff'], [1, '#22c55e']];

const fmtPct = (v) => (v != null ? (v * 100).toFixed(1) + '%' : 'N/A');
const fmtNum = (v) => (v != null ? (typeof v === 'number' ? v.toFixed(2) : v) : 'N/A');
const valColor = (v) => (v > 0 ? 'text-green-400' : v < 0 ? 'text-red-400' : 'text-gray-400');

const StrategyDetailPage = ({ analyticsParams, paramsKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState('');

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { ...analyticsParams };
        if (selectedStrategy) params.focus = selectedStrategy;
        const res = await tradingService.analyticsDetail(params);
        if (!cancelled) {
          setData(res.data);
          // Set default selected strategy
          if (!selectedStrategy && res.data.available_strategies?.length > 0) {
            setSelectedStrategy(res.data.available_strategies[0]);
          }
        }
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, selectedStrategy]);

  const strategyColor = data?.color || '#c9a96e';

  // Metric cards
  const metricCards = useMemo(() => {
    if (!data?.metrics) return [];
    const m = data.metrics;
    return [
      { label: 'Ann. Return', value: fmtPct(m.ann_return), color: valColor(m.ann_return) },
      { label: 'Ann. Std Dev', value: fmtPct(m.ann_std), color: 'text-gray-300' },
      { label: 'Sharpe', value: fmtNum(m.sharpe), color: valColor(m.sharpe) },
      { label: 'Max Drawdown', value: fmtPct(m.max_drawdown), color: 'text-red-400' },
      { label: '% Pos Months', value: fmtPct(m.pct_positive), color: m.pct_positive > 0.5 ? 'text-green-400' : 'text-red-400' },
      { label: 'Best / Worst', value: `${fmtPct(m.best_month)} / ${fmtPct(m.worst_month)}`, color: 'text-gray-300' },
    ];
  }, [data]);

  // Monthly returns bar chart
  const monthlyBarTrace = useMemo(() => {
    if (!data?.monthly_returns) return null;
    const { index, values } = data.monthly_returns;
    const pairs = (index || []).map((d, i) => [d, values?.[i]])
      .filter(([, v]) => v != null);
    if (!pairs.length) return null;
    return {
      x: pairs.map(([d]) => d),
      y: pairs.map(([, v]) => v),
      type: 'bar',
      marker: { color: pairs.map(([, v]) => (v >= 0 ? '#22c55e' : '#dc2626')) },
      hovertemplate: '%{x|%b %Y}: %{y:.2%}<extra></extra>',
    };
  }, [data]);

  // Cumulative returns with SPX overlay
  const cumTraces = useMemo(() => {
    if (!data?.cumulative) return [];
    const traces = [];
    const { index: dates, data: rows, columns } = data.cumulative;

    if (dates && dates.length > 0) {
      // Single-column or multi-column cumulative
      const yVals = rows.map((row) => (Array.isArray(row) ? row[0] : row));
      const xArr = [], yArr = [];
      dates.forEach((d, i) => {
        if (yVals[i] != null) { xArr.push(d); yArr.push(yVals[i]); }
      });
      traces.push({
        x: xArr, y: yArr, type: 'scatter', mode: 'lines',
        name: selectedStrategy || (columns?.[0] || 'Strategy'),
        line: { color: strategyColor, width: 2 },
        hovertemplate: '%{y:.2%}<extra></extra>',
      });
    }

    // SPX overlay
    if (data.spx_cumulative) {
      const { index: spxDates, data: spxRows } = data.spx_cumulative;
      const spxVals = spxRows.map((row) => (Array.isArray(row) ? row[0] : row));
      const xArr = [], yArr = [];
      spxDates.forEach((d, i) => {
        if (spxVals[i] != null) { xArr.push(d); yArr.push(spxVals[i]); }
      });
      if (xArr.length > 0) {
        traces.push({
          x: xArr, y: yArr, type: 'scatter', mode: 'lines',
          name: 'SPX Index', line: { color: '#DC143C', width: 2, dash: 'dash' },
          hovertemplate: 'SPX: %{y:.2%}<extra></extra>',
        });
      }
    }

    return traces;
  }, [data, selectedStrategy, strategyColor]);

  // Drawdown area chart
  const drawdownTrace = useMemo(() => {
    if (!data?.drawdown) return null;
    const { index: dates, data: rows } = data.drawdown;
    const xArr = [], yArr = [];
    dates.forEach((d, i) => {
      const v = Array.isArray(rows[i]) ? rows[i][0] : rows[i];
      if (v != null) { xArr.push(d); yArr.push(v); }
    });
    if (!xArr.length) return null;
    return {
      x: xArr, y: yArr, type: 'scatter', mode: 'lines',
      fill: 'tozeroy', fillcolor: 'rgba(220, 38, 38, 0.2)',
      line: { color: '#dc2626', width: 1 },
      hovertemplate: 'Drawdown: %{y:.2%}<extra></extra>',
    };
  }, [data]);

  // Monthly heatmap (years x months)
  const heatmapData = useMemo(() => {
    if (!data?.monthly_heatmap) return null;
    const { index: rawYears, columns: rawMonths, data: rows } = data.monthly_heatmap;
    if (!rawYears.length) return null;

    const years = rawYears.map(String);
    const months = rawMonths.length > 0 ? rawMonths.map((c, i) => MONTHS[i] || c) : MONTHS;

    return {
      z: rows, x: months, y: years,
      type: 'heatmap', colorscale: HEATMAP_SCALE,
      zmid: 0,
      text: rows.map((row) => row.map((v) => (v != null ? (v * 100).toFixed(1) + '%' : ''))),
      texttemplate: '%{text}',
      hovertemplate: '%{y} %{x}: %{z:.2%}<extra></extra>',
      showscale: true,
      colorbar: { tickformat: '.0%', len: 0.8 },
    };
  }, [data]);

  // Distribution histogram
  const distributionTrace = useMemo(() => {
    if (!data?.distribution) return null;
    const vals = Array.isArray(data.distribution)
      ? data.distribution
      : data.distribution.values || data.distribution.data || [];
    return {
      x: Array.isArray(vals[0]) ? vals.map((r) => r[0]) : vals,
      type: 'histogram', nbinsx: 30,
      marker: { color: strategyColor, opacity: 0.7 },
      hovertemplate: 'Return: %{x:.2%}<br>Count: %{y}<extra></extra>',
    };
  }, [data, strategyColor]);

  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none w-full';
  const chartLayout = {
    paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
    font: { color: '#9ca3af', size: 10 },
  };

  if (loading && !data) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  const availableStrategies = data.available_strategies || [];

  return (
    <div>
      {/* Strategy Selector */}
      <div className="mb-4 max-w-sm">
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Select Strategy</label>
        <select
          className={selectCls}
          value={selectedStrategy}
          onChange={(e) => setSelectedStrategy(e.target.value)}
        >
          {availableStrategies.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Metric Cards */}
      {metricCards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {metricCards.map((mc) => (
            <div key={mc.label} className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{mc.label}</p>
              <p className={`text-lg font-bold tabular-nums ${mc.color}`}>{mc.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Monthly Returns Bar Chart */}
      {monthlyBarTrace && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Monthly Returns</h3>
          <PlotlyChart
            data={[monthlyBarTrace]}
            layout={{ ...chartLayout, yaxis: { title: 'Monthly Return', tickformat: '.1%' }, showlegend: false }}
            style={{ minHeight: 300 }}
          />
        </div>
      )}

      {/* Cumulative Return with SPX */}
      {cumTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Cumulative Return vs SPX</h3>
          <PlotlyChart
            data={cumTraces}
            layout={{
              ...chartLayout,
              yaxis: { title: 'Cumulative Return', tickformat: '.0%' },
              legend: { orientation: 'h', yanchor: 'bottom', y: -0.2, xanchor: 'center', x: 0.5 },
            }}
            style={{ minHeight: 320 }}
          />
        </div>
      )}

      {/* Drawdown & Distribution side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {drawdownTrace && (
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Drawdown</h3>
            <PlotlyChart
              data={[drawdownTrace]}
              layout={{ ...chartLayout, yaxis: { title: 'Drawdown', tickformat: '.0%' }, showlegend: false }}
              style={{ minHeight: 280 }}
            />
          </div>
        )}

        {distributionTrace && (
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Return Distribution</h3>
            <PlotlyChart
              data={[distributionTrace]}
              layout={{
                ...chartLayout,
                yaxis: { title: 'Frequency', tickformat: 'd' },
                xaxis: { title: 'Monthly Return', tickformat: '.1%' },
                showlegend: false,
              }}
              style={{ minHeight: 280 }}
            />
          </div>
        )}
      </div>

      {/* Monthly Heatmap */}
      {heatmapData && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Monthly Returns Heatmap</h3>
          <div className="overflow-x-auto">
            <PlotlyChart
              data={[heatmapData]}
              interactive
              layout={{
                ...chartLayout,
                yaxis: { autorange: 'reversed', dtick: 1 },
                xaxis: { side: 'top', dtick: 1 },
                margin: { l: 60, r: 40, t: 40, b: 20 },
              }}
              style={{ minHeight: Math.max(350, (heatmapData.y?.length || 5) * 30 + 100) }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyDetailPage;
